// Наличността на листа „Изписване" като ЕДНА сметка за целия документ: колко
// от всеки продукт иска документът (продуктовите редове + съставките на
// рецептите × порции), колко има КЪМ ДАТАТА в склада и кое не стига — същата
// проверка като на BE (`assert_sufficient_stock`: нуждата е СЪБРАНА по продукт),
// за да се види ПРЕДИ записа и при коя рецепта. Тук е и стойността на
// неразделена рецепта: себестойността на съставките ѝ по партидите/FIFO — същата
// основа като на продуктовите редове. Чисти функции, без React.
import type { TFunction } from "i18next";
import type { Product, RecipeIngredient, StockAsOf } from "../../../../api/inventory";
import { clampDecimals } from "../../components/qty";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import {
  fifoUnitPrice,
  formatPortionsInput,
  parseDecimal,
  type WriteoffLineDraft,
} from "./writeoffWorksheetShared";

// Плаващата запетая: 0,1 кг × 3 порции = 0,30000000000000004 — не е недостиг
const QUANTITY_EPSILON = 1e-9;
// Количествата се пазят с 4 знака — с толкова се показват нужно и налично,
// които в знаците на единицата изглеждат еднакво (както в текста на BE)
const STORED_QUANTITY_DECIMALS = 4;
// Колко недостигащи назовава пречката във футъра (останалите — „и още N")
const ISSUE_ITEMS_LIMIT = 2;

export type IngredientsByRecipe = Map<number, RecipeIngredient[]>;

// Откъде идва нуждата от продукт: продуктов ред или съставка на ред-рецепта
export type NeedSource =
  | { kind: "product"; lineKey: number; quantity: number }
  | { kind: "recipe"; lineKey: number; recipeName: string; portions: number; quantity: number };

export type ProductNeed = {
  productId: number;
  needed: number;
  // името и мярката от съставката — за продукт извън справочника на листа
  fallbackName: string | null;
  fallbackUnit: string | null;
  sources: NeedSource[];
};

// Продукт, който не стига КЪМ ДАТАТА в склада на документа
export type StockShortage = {
  productId: number;
  name: string;
  unit: string | null;
  decimals: number;
  needed: number;
  // клампнато до 0 — наличността не се показва отрицателна
  available: number;
  missing: number;
  sources: NeedSource[];
};

// Стойността на неразделена рецепта (null — съставките или наличността им
// още се зареждат; празна рецепта)
export type RecipeLineValues = {
  unitPrice: number | null; // себестойност на ЕДНА порция
  netSum: number | null;
  grossSum: number | null;
};

// Бруто по регистрацията по ДДС на фирмата — същото правило като продуктовия ред
export const grossOfNet = (net: number, vatRate: number | null | undefined, vatRegistered: boolean) =>
  vatRegistered && vatRate ? net * (1 + vatRate / 100) : net;

// Колко от всеки продукт иска документът. Рецепта, чиито съставки още се
// зареждат, не влиза (fail-open, както наличност в процес на зареждане).
export const buildProductNeeds = (
  lines: WriteoffLineDraft[],
  ingredientsByRecipe: IngredientsByRecipe,
): Map<number, ProductNeed> => {
  const needs = new Map<number, ProductNeed>();
  const add = (productId: number, source: NeedSource, ingredient?: RecipeIngredient) => {
    const current = needs.get(productId);
    if (current) {
      current.needed += source.quantity;
      current.sources.push(source);
      current.fallbackName ??= ingredient?.productName || null;
      current.fallbackUnit ??= ingredient?.unit ?? null;
      return;
    }
    needs.set(productId, {
      productId,
      needed: source.quantity,
      fallbackName: ingredient?.productName || null,
      fallbackUnit: ingredient?.unit ?? null,
      sources: [source],
    });
  };
  for (const line of lines) {
    const quantity = parseDecimal(line.quantity);
    if (!(quantity > 0)) continue;
    if (line.kind === "product") {
      if (line.productId != null) add(line.productId, { kind: "product", lineKey: line.key, quantity });
      continue;
    }
    const ingredients = line.recipeId != null ? ingredientsByRecipe.get(line.recipeId) : undefined;
    for (const ingredient of ingredients ?? []) {
      const need = ingredient.quantity * quantity;
      if (!(need > 0)) continue;
      add(
        ingredient.productId,
        { kind: "recipe", lineKey: line.key, recipeName: line.recipeName ?? "", portions: quantity, quantity: need },
        ingredient,
      );
    }
  }
  return needs;
};

// Кое не стига — по реда на документа; само за заредена наличност (иначе би
// мигало фалшиво „не стига", докато заявката тече). Сравнението е със ЗНАКОВАТА
// наличност, както на BE: при минус не стига нищо.
export const findStockShortages = (
  needs: Map<number, ProductNeed>,
  asOfByProduct: Map<number, StockAsOf>,
  productById: Map<number, Product>,
): StockShortage[] => {
  const shortages: StockShortage[] = [];
  for (const need of needs.values()) {
    const asOf = asOfByProduct.get(need.productId);
    if (!asOf || need.needed - asOf.quantity <= QUANTITY_EPSILON) continue;
    const product = productById.get(need.productId);
    const available = Math.max(0, asOf.quantity);
    shortages.push({
      productId: need.productId,
      name: product?.name ?? need.fallbackName ?? `#${need.productId}`,
      unit: asOf.unit ?? product?.unit ?? need.fallbackUnit ?? null,
      decimals: clampDecimals(product?.decimalPlaces),
      needed: need.needed,
      available,
      missing: need.needed - available,
      sources: need.sources,
    });
  }
  return shortages;
};

// Недостигът по продукт — за продуктовия ред (червено + „нужни общо")
export const shortageByProductOf = (shortages: StockShortage[]) =>
  new Map(shortages.map((shortage) => [shortage.productId, shortage]));

// Недостигите, в които участва даден ред (редът-рецепта → съставките му)
export const shortagesOfLine = (lineKey: number, shortages: StockShortage[]) =>
  shortages.filter((shortage) => shortage.sources.some((source) => source.lineKey === lineKey));

// Колко от нуждата идва от този ред (при съставка, поискана и от други редове)
export const lineShareOf = (shortage: StockShortage, lineKey: number) =>
  shortage.sources
    .filter((source) => source.lineKey === lineKey)
    .reduce((sum, source) => sum + source.quantity, 0);

// Колко ЦЕЛИ порции от рецептата стигат при нуждите на ОСТАНАЛИТЕ редове
// (null — празна рецепта или наличност, която още се зарежда)
export const fittingPortions = (
  line: WriteoffLineDraft,
  ingredients: RecipeIngredient[] | undefined,
  needs: Map<number, ProductNeed>,
  asOfByProduct: Map<number, StockAsOf>,
): number | null => {
  if (!ingredients) return null;
  const portions = Math.max(0, parseDecimal(line.quantity));
  let fit = Infinity;
  for (const ingredient of ingredients) {
    if (!(ingredient.quantity > 0)) continue;
    const asOf = asOfByProduct.get(ingredient.productId);
    if (!asOf) return null;
    const otherNeed = (needs.get(ingredient.productId)?.needed ?? 0) - ingredient.quantity * portions;
    const room = Math.max(0, asOf.quantity) - otherNeed;
    fit = Math.min(fit, room / ingredient.quantity);
  }
  if (fit === Infinity) return null;
  return Math.max(0, Math.floor(fit + QUANTITY_EPSILON));
};

// Стойността на ред-рецепта: всяка съставка × порциите по ЦЕНАТА на партидите
// към датата (FIFO, претеглена — както продуктовия ред), брутото — по ДДС
// ставката на всяка съставка. Без написани порции → цената на ЕДНА порция, без
// суми. Съставка без партиди (няма наличност) не носи цена — недостигът я назовава.
export const recipeLineValues = (
  line: WriteoffLineDraft,
  ingredients: RecipeIngredient[] | undefined,
  asOfByProduct: Map<number, StockAsOf>,
  productById: Map<number, Product>,
  vatRegistered: boolean,
): RecipeLineValues => {
  const unknown: RecipeLineValues = { unitPrice: null, netSum: null, grossSum: null };
  const used = (ingredients ?? []).filter((ingredient) => ingredient.quantity > 0);
  if (!used.length) return unknown;
  const portions = parseDecimal(line.quantity);
  const basis = portions > 0 ? portions : 1;
  let net = 0;
  let gross = 0;
  for (const ingredient of used) {
    const asOf = asOfByProduct.get(ingredient.productId);
    if (!asOf) return unknown;
    const quantity = ingredient.quantity * basis;
    const unitPrice = fifoUnitPrice(asOf.lots, quantity, null);
    if (unitPrice == null) continue;
    const ingredientNet = quantity * unitPrice;
    net += ingredientNet;
    gross += grossOfNet(ingredientNet, productById.get(ingredient.productId)?.vatRate, vatRegistered);
  }
  return {
    unitPrice: net / basis,
    netSum: portions > 0 ? net : null,
    grossSum: portions > 0 ? gross : null,
  };
};

// ─── Текстовете ─────────────────────────────────────────────────────────────
// Нужно и налично в знаците на единицата; закръглени еднакво (0,1234 срещу
// 0,123 кг) → с 4 знака, иначе недостигът не се разбира
export const shortageQuantities = (shortage: StockShortage) => {
  const format = (value: number, decimals: number) => formatQtyUnit(value, decimals, shortage.unit);
  const needed = format(shortage.needed, shortage.decimals);
  const available = format(shortage.available, shortage.decimals);
  if (needed !== available || shortage.decimals >= STORED_QUANTITY_DECIMALS) return { needed, available };
  return {
    needed: format(shortage.needed, STORED_QUANTITY_DECIMALS),
    available: format(shortage.available, STORED_QUANTITY_DECIMALS),
  };
};

// „−0,700 кг" — колко не достига; под знаците на единицата → с 4 знака
export const shortageMissingText = (shortage: StockShortage) => {
  const zero = formatQtyUnit(0, shortage.decimals, shortage.unit);
  const text = formatQtyUnit(shortage.missing, shortage.decimals, shortage.unit);
  const shown =
    text === zero && shortage.decimals < STORED_QUANTITY_DECIMALS
      ? formatQtyUnit(shortage.missing, STORED_QUANTITY_DECIMALS, shortage.unit)
      : text;
  return `−${shown}`;
};

// „Шопска салата ×2" — рецептите, от които идва нуждата (без повторения)
export const recipeSourcesOf = (shortage: StockShortage) => [
  ...new Set(
    shortage.sources.flatMap((source) =>
      source.kind === "recipe" ? [`${source.recipeName} ×${formatPortionsInput(source.portions)}`] : [],
    ),
  ),
];

// „Домати — нужни 1,200 кг, налични 0,500 кг (за Шопска салата ×2)"
export const shortageLineText = (t: TFunction, shortage: StockShortage) => {
  const { needed, available } = shortageQuantities(shortage);
  const recipes = recipeSourcesOf(shortage);
  return recipes.length
    ? t("inventoryPage.wr6ShortItemFrom", { name: shortage.name, needed, available, recipes: recipes.join(", ") })
    : t("inventoryPage.wr6ShortItem", { name: shortage.name, needed, available });
};

// Пречката във футъра: първите два недостигащи поименно, останалите — „и още N"
export const shortageIssueText = (t: TFunction, shortages: StockShortage[]) => {
  const named = shortages.slice(0, ISSUE_ITEMS_LIMIT).map((shortage) => shortageLineText(t, shortage));
  const rest = shortages.length - named.length;
  const items = rest > 0 ? `${named.join("; ")} ${t("inventoryPage.wr6ShortMore", { count: rest })}` : named.join("; ");
  return t("inventoryPage.wr6IssueShort", { items });
};
